import React, { useEffect, useRef, useState } from 'react';
import FullscreenControl from 'react-leaflet-fullscreen';
import { Form, Icon as LegacyIcon } from '@ant-design/compatible';
import { Switch } from 'antd';
import '@ant-design/compatible/assets/index.css';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { geocodeByPlaceId } from 'react-google-places-autocomplete';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Descriptions,
  Divider,
  Empty,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Table,
  Tabs,
  Tag,
  TimePicker,
} from 'antd';
import { FormComponentProps } from '@ant-design/compatible/lib/form';
import CreateAmount from '../CreateAmount/CreateAmount';
import moment from 'moment';
import { ICreateAmountFormValues } from '../CreateAmount/CreateAmountForm';
import UUID from '../../../utils/uuid';
import {
  ComputeItineraryPriceDocument,
  ComputeItineraryPriceQuery,
  ComputeItineraryPriceQueryVariables,
  GetBudgetQuery,
  GetClientRateQuery,
  ServiceTypesQuery,
  Vat,
  GetVehicleBrandModelsQuery,
  VehicleTypesQuery,
} from '../../../graphql/datamodel.gen';
import EditAmount from '../EditAmount/EditAmount';
import CreateItineraryFeature from '../CreateItineraryFeature/CreateItineraryFeature';
import EditItineraryFeature from '../EditItineraryFeature/EditItineraryFeature';
import { ICreateItineraryFeatureFormValues } from '../CreateItineraryFeature/CreateItineraryFeatureForm';
import StickyFooterFormButtons from '../../StickyFooterFormButtons/StickyFooterFormButtons';
import AmountUtils from '../CreateAmount/utils';
import { BudgetConfig } from '../types';
import { capitalizeFirstChar } from '../../../utils/string';
import _ from 'lodash';
import * as Leaflet from 'react-leaflet';
import L from 'leaflet';
import RoutingMachine from '../../maps/RoutingMachine';
import { getClient } from '../../../apollo';
import { MainContext } from '../../../contexts/mainContext';

interface ICreateItineraryFormValues {
  departureTime: moment.Moment;
  arrivalTime: moment.Moment;
  departureArrivalDate: moment.Moment[];
  departureArrivalTime: moment.Moment[];
  presentationTime: moment.Moment;
  serviceTypeId: string;
  reference?: string;
  numVehicles?: number;
  numPassengers?: number;
  itineraryDescription: string;
  groupName?: string;
  flightNumber?: string;
  notes?: string;
  dinner?: boolean;
  vehicleCategoryId: string;
}

export interface ItineraryCandidate
  extends Omit<
    ICreateItineraryFormValues,
    'departureArrivalDate' | 'departureArrivalTime' | 'vehicleCategoryId'
  > {
  id: string;
  realized?: boolean;
  amounts: Amount[];
  driverFeatures: FeatureCandidate[];
  vehicleFeatures: FeatureCandidate[];
  _notSubmitted?: boolean;
  departureGoogleMapsPlaceID?: string;
  departureType?: string;
  departureAddress?: string;
  departureLat?: number;
  departureLon?: number;
  departureCity?: string;

  provisionGoogleMapsPlaceID?: string;
  provisionType?: string;
  provisionAddress?: string;
  provisionLat?: number;
  provisionLon?: number;
  provisionCity?: string;

  arrivalGoogleMapsPlaceID?: string;
  arrivalType?: string;
  arrivalAddress?: string;
  arrivalLat?: number;
  arrivalLon?: number;
  arrivalCity?: string;
  vehicleCategory?: GetBudgetQuery['budget']['itineraries'][0]['vehicleCategory'];
}

export interface Amount {
  id: string;
  concept?: string;
  amount: number;
  vat: number;
}

interface FeatureCandidate extends ICreateItineraryFeatureFormValues {
  id: string;
}

interface ICreateItineraryFormProps extends FormComponentProps {
  budgetConfig: BudgetConfig;
  allVehicleBrandModels: GetVehicleBrandModelsQuery['vehicleBrandModels'];
  allVehicleTypes: VehicleTypesQuery['vehicleTypes'];
  itinerary?: ItineraryCandidate;
  serviceTypes: ServiceTypesQuery['serviceTypes'][number][];
  rate: GetClientRateQuery['client']['rate'];
  onSubmit: (
    values: Partial<Pick<ItineraryCandidate, 'id'>> &
      Omit<ItineraryCandidate, 'id'>,
  ) => any;
  close: () => any;
  vatTypes: Vat[];
  clientId: string;
}

interface ICreateItineraryFormState {
  draft: boolean;
  errors?: {
    [field: string]: {
      errors: {
        message: string;
        field: string;
      }[];
    };
  };
  createAmountModalVisible: boolean;
  editAmountModalVisible: boolean;
  createItineraryFeatureVisible: boolean;
  editItineraryFeatureVisible: boolean;
  amounts: Amount[];
  selectedAmounts: string[];
  driverFeatures: FeatureCandidate[];
  selectedDriverFeatures: string[];
  vehicleFeatures: FeatureCandidate[];
  selectedVehicleFeatures: string[];
  featureType: 'vehicle' | 'driver' | undefined;
  departureGoogleMapsPlaceID?: string;
  departureAddress?: string;
  departureCity?: string;
  departureType?: string;
  departureLat?: number;
  departureLon?: number;
  departureTime?: moment.Moment;
  departureDate?: moment.Moment;
  departureDateTime?: moment.Moment;

  provisionGoogleMapsPlaceID?: string;
  provisionAddress?: string;
  provisionCity?: string;
  provisionType?: string;
  provisionLat?: number;
  provisionLon?: number;
  provisionTime?: moment.Moment;
  provisionDate?: moment.Moment;
  provisionDateTime?: moment.Moment;

  arrivalGoogleMapsPlaceID?: string;
  arrivalAddress?: string;
  arrivalCity?: string;
  arrivalType?: string;
  arrivalLat?: number;
  arrivalLon?: number;
  arrivalTime?: moment.Moment;
  arrivalDate?: moment.Moment;
  arrivalDateTime?: moment.Moment;
  estimatedDistanceInMeters?: number;
  selectedServiceTypeCode?: string;
  selectedVehicleCategoryId?: string;
  dinner: boolean;
  selectedServiceTypeId?: string;
  calculatingPrice: boolean;
  selectedNumPassengers?: number;
}

interface MiniMapProps {
  departure?: { lat: number; lon: number };
  arrival?: { lat: number; lon: number };
  provision?: { lat: number; lon: number };
  onDistanceFound?: (distanceInMeters: number) => any;
}

const MiniMap: React.FC<MiniMapProps> = (props: MiniMapProps) => {
  const [mapReady, setMapReady] = useState(false);

  const mapRef = useRef();

  useEffect(() => {
    // @ts-ignore
    mapRef?.current?.leafletElement?.invalidateSize?.();
  }, [mapReady]);

  const route = [];
  if (props.departure) {
    route.push(L.latLng(props.departure.lat, props.departure.lon));
  }
  if (props.arrival) {
    route.push(L.latLng(props.arrival.lat, props.arrival.lon));
  }

  if (props.provision) {
    route.push(L.latLng(props.provision.lat, props.provision.lon));
  }

  return (
    <div
      id={'container'}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        borderRadius: 5,
        overflow: 'hidden',
      }}
    >
      <Leaflet.Map
        ref={mapRef}
        preferCanvas={true}
        center={[41.39701, 2.169457]}
        zoom={12}
        style={{
          width: '100%',
          height: '100%',
          borderRadius: 5,
        }}
        whenReady={() => {
          // @ts-ignore
          setMapReady(true);
        }}
        keyboard={false}
      >
        <Leaflet.TileLayer
          updateWhenZooming={true}
          detectRetina={true}
          maxZoom={19}
          keepBuffer={16}
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          subdomains="abcd"
        />
        <FullscreenControl position="topleft" />
        <RoutingMachine
          onDistanceFound={props.onDistanceFound}
          // @ts-ignore
          color="#1890ff"
          map={mapRef}
          road={route}
        />
      </Leaflet.Map>
    </div>
  );
};

class CreateItineraryForm extends React.Component<
  ICreateItineraryFormProps,
  ICreateItineraryFormState
> {
  static contextType = MainContext;

  state: ICreateItineraryFormState = {
    draft: false,
    errors: undefined,
    selectedServiceTypeCode: undefined,
    createAmountModalVisible: false,
    editAmountModalVisible: false,
    createItineraryFeatureVisible: false,
    editItineraryFeatureVisible: false,
    amounts: [],
    selectedAmounts: [],
    driverFeatures: [],
    selectedDriverFeatures: [],
    vehicleFeatures: [],
    selectedVehicleFeatures: [],
    featureType: undefined,
    departureGoogleMapsPlaceID: undefined,
    departureType: undefined,
    departureCity: undefined,
    departureAddress: undefined,
    departureLat: undefined,
    departureLon: undefined,
    departureDate: undefined,
    departureTime: undefined,
    departureDateTime: undefined,

    provisionGoogleMapsPlaceID: undefined,
    provisionType: undefined,
    provisionCity: undefined,
    provisionAddress: undefined,
    provisionLat: undefined,
    provisionLon: undefined,

    arrivalGoogleMapsPlaceID: undefined,
    arrivalType: undefined,
    arrivalAddress: undefined,
    arrivalCity: undefined,
    arrivalLat: undefined,
    arrivalLon: undefined,
    arrivalTime: undefined,
    arrivalDate: undefined,
    arrivalDateTime: undefined,
    estimatedDistanceInMeters: undefined,
    selectedVehicleCategoryId: undefined,
    dinner: false,
    selectedServiceTypeId: undefined,
    calculatingPrice: false,
    selectedNumPassengers: undefined,
  };

  constructor(props: Readonly<ICreateItineraryFormProps>) {
    super(props);

    // @ts-ignore
    this.state = {
      draft: false,
      errors: undefined,
      selectedNumPassengers: this.props.itinerary?.numPassengers || 0,
      selectedServiceTypeCode: this.props.serviceTypes.find(
        (st) => st.id === this.props.itinerary?.serviceTypeId,
      )?.typeId,
      selectedServiceTypeId: this.props.itinerary?.serviceTypeId,
      createAmountModalVisible: false,
      editAmountModalVisible: false,
      createItineraryFeatureVisible: false,
      editItineraryFeatureVisible: false,
      amounts: this.props.itinerary?.amounts || [],
      selectedAmounts: [],
      driverFeatures: this.props.itinerary?.driverFeatures || [],
      selectedDriverFeatures: [],
      vehicleFeatures: this.props.itinerary?.vehicleFeatures || [],
      selectedVehicleFeatures: [],
      featureType: undefined,
      departureGoogleMapsPlaceID:
        this.props.itinerary?.departureGoogleMapsPlaceID,
      departureType: this.props.itinerary?.departureType,
      departureLat: this.props.itinerary?.departureLat,
      departureLon: this.props.itinerary?.departureLon,
      departureAddress: this.props.itinerary?.departureAddress,
      departureTime: this.props.itinerary?.departureTime,
      departureDateTime: this.props.itinerary?.departureTime,
      departureDate: this.props.itinerary?.departureTime,
      departureCity: this.props.itinerary?.departureCity,

      provisionGoogleMapsPlaceID:
        this.props.itinerary?.provisionGoogleMapsPlaceID,
      provisionType: this.props.itinerary?.provisionType,
      provisionLat: this.props.itinerary?.provisionLat,
      provisionLon: this.props.itinerary?.provisionLon,
      provisionAddress: this.props.itinerary?.provisionAddress,
      provisionCity: this.props.itinerary?.provisionCity,

      arrivalGoogleMapsPlaceID: this.props.itinerary?.arrivalGoogleMapsPlaceID,
      arrivalType: this.props.itinerary?.arrivalType,
      arrivalLat: this.props.itinerary?.arrivalLat,
      arrivalLon: this.props.itinerary?.arrivalLon,
      arrivalAddress: this.props.itinerary?.arrivalAddress,
      arrivalTime: this.props.itinerary?.arrivalTime,
      arrivalCity: this.props.itinerary?.arrivalCity,
      arrivalDateTime: this.props.itinerary?.arrivalTime,
      arrivalDate: this.props.itinerary?.arrivalTime,
      selectedVehicleCategoryId:
        this.props.itinerary?.vehicleCategory?.id ||
        //@ts-ignore
        this.props.itinerary?.vehicleCategoryId,
      estimatedDistanceInMeters: undefined,
      dinner: this.props.itinerary?.dinner || false,
      calculatingPrice: false,
    };
  }

  componentDidUpdate(
    prevProps: Readonly<ICreateItineraryFormProps>,
    prevState: Readonly<ICreateItineraryFormState>,
    snapshot?: any,
  ): void {
    // if (
    //   (!!prevState.departureDate &&
    //     !prevState.departureDate.isSame(this.state.departureDate)) ||
    //   (!!this.state.departureDate &&
    //     !this.state.departureDate.isSame(prevState.departureDate))
    // ) {
    //   this.calculateAmount();
    // }

    // if (
    //   (!!prevState.departureTime &&
    //     !prevState.departureTime.isSame(this.state.departureTime)) ||
    //   (!!this.state.departureTime &&
    //     !this.state.departureTime.isSame(prevState.departureTime))
    // ) {
    //   this.calculateAmount();
    // }

    if (
      prevState.departureGoogleMapsPlaceID !==
        this.state.departureGoogleMapsPlaceID ||
      prevState.departureType !== this.state.departureType
    ) {
      this.calculateAmount();
    }

    // if (
    //   (!!prevState.arrivalDate &&
    //     !prevState.arrivalDate.isSame(this.state.arrivalDate)) ||
    //   (!!this.state.arrivalDate &&
    //     !this.state.arrivalDate.isSame(prevState.arrivalDate))
    // ) {
    //   this.calculateAmount();
    // }

    // if (
    //   (!!prevState.arrivalTime &&
    //     !prevState.arrivalTime.isSame(this.state.arrivalTime)) ||
    //   (!!this.state.arrivalTime &&
    //     !this.state.arrivalTime.isSame(prevState.arrivalTime))
    // ) {
    //   this.calculateAmount();
    // }

    if (prevState.dinner !== this.state.dinner) {
      this.calculateAmount();
    }

    if (prevState.selectedServiceTypeId !== this.state.selectedServiceTypeId) {
      this.calculateAmount();
    }

    if (
      prevState.selectedVehicleCategoryId !==
      this.state.selectedVehicleCategoryId
    ) {
      this.calculateAmount();
    }

    if (
      prevState.arrivalGoogleMapsPlaceID !==
        this.state.arrivalGoogleMapsPlaceID ||
      prevState.arrivalType !== this.state.arrivalType
    ) {
      this.calculateAmount();
    }

    if (
      (!!prevState.provisionDate &&
        !prevState.provisionDate.isSame(this.state.provisionDate)) ||
      (!!this.state.provisionDate &&
        !this.state.provisionDate.isSame(prevState.provisionDate))
    ) {
      this.calculateAmount();
    }

    if (
      (!!prevState.provisionTime &&
        !prevState.provisionTime.isSame(this.state.provisionTime)) ||
      (!!this.state.provisionTime &&
        !this.state.provisionTime.isSame(prevState.provisionTime))
    ) {
      this.calculateAmount();
    }

    if (
      prevState.provisionGoogleMapsPlaceID !==
        this.state.provisionGoogleMapsPlaceID ||
      prevState.provisionType !== this.state.provisionType
    ) {
      this.calculateAmount();
    }
  }

  placesInformationFilledIn = (): boolean => {
    const departureAndArrivalFilledIn =
      !!this.state.departureGoogleMapsPlaceID &&
      !!this.state.arrivalGoogleMapsPlaceID;

    if (this.state.selectedServiceTypeCode === 'TR') {
      return departureAndArrivalFilledIn;
    }

    if (this.state.selectedServiceTypeCode === 'DIS') {
      return (
        departureAndArrivalFilledIn && !!this.state.provisionGoogleMapsPlaceID
      );
    }

    return false;
  };

  handleSubmit = (e: any) => {
    e.preventDefault();
    this.props.form.validateFields(
      (err, values: ICreateItineraryFormValues) => {
        if (!err && this.placesInformationFilledIn()) {
          const departureDate = values.departureArrivalDate[0];
          const arrivalDate = values.departureArrivalDate[1];

          const departureTime = values.departureArrivalTime[0];
          const arrivalTime = values.departureArrivalTime[1];

          const departureDateTime = departureDate
            .clone()
            .set('hours', departureTime.get('hours'))
            .set('minutes', departureTime.get('minutes'));

          const arrivalDateTime = arrivalDate
            .clone()
            .set('hours', arrivalTime.get('hours'))
            .set('minutes', arrivalTime.get('minutes'));

          const {
            departureArrivalDate,
            departureArrivalTime,
            vehicleCategoryId,
            ...cleanedValues
          } = values;

          // const selectedVehicleCategory =
          //   this.props.rate.vehicleCategories.find(
          //     (vc) => vc.id === values.vehicleCategoryId,
          //   );

          const itinerary: Partial<Pick<ItineraryCandidate, 'id'>> &
            Omit<ItineraryCandidate, 'id'> & { vehicleCategoryId?: string } = {
            ...cleanedValues,
            _notSubmitted: true,
            serviceTypeId: values.serviceTypeId,
            presentationTime: departureDateTime,
            amounts: this.state.amounts,
            dinner: this.state.dinner,
            vehicleFeatures: this.state.vehicleFeatures,
            driverFeatures: this.state.driverFeatures,
            departureGoogleMapsPlaceID: this.state.departureGoogleMapsPlaceID,
            departureType: this.state.departureType,
            departureAddress: this.state.departureAddress,
            departureTime: departureDateTime,
            departureLat: this.state.departureLat,
            departureLon: this.state.departureLon,
            departureCity: this.state.departureCity,

            provisionGoogleMapsPlaceID: this.state.provisionGoogleMapsPlaceID,
            provisionType: this.state.provisionType,
            provisionAddress: this.state.provisionAddress,
            provisionLat: this.state.provisionLat,
            provisionLon: this.state.provisionLon,
            provisionCity: this.state.provisionCity,

            arrivalGoogleMapsPlaceID: this.state.arrivalGoogleMapsPlaceID,
            arrivalType: this.state.arrivalType,
            arrivalTime: arrivalDateTime,
            arrivalAddress: this.state.arrivalAddress,
            arrivalCity: this.state.arrivalCity,
            arrivalLat: this.state.arrivalLat,
            arrivalLon: this.state.arrivalLon,
            vehicleCategoryId: this.state.selectedVehicleCategoryId,
          };

          this.props.onSubmit(itinerary);
        } else {
          this.setState({
            errors: err,
          });
        }
      },
    );
  };

  openCreateAmountModal = () => {
    this.setState({
      createAmountModalVisible: true,
    });
  };

  hideCreateAmountModal = () => {
    this.setState({
      createAmountModalVisible: false,
    });
  };

  openCreateItineraryFeatureModal = (type: 'vehicle' | 'driver') => {
    this.setState({
      createItineraryFeatureVisible: true,
      featureType: type,
    });
  };

  hideCreateItineraryFeatureModal = () => {
    this.setState({
      createItineraryFeatureVisible: false,
      featureType: undefined,
    });
  };

  openEditItineraryFeatureModal = (type: 'vehicle' | 'driver') => {
    this.setState({
      editItineraryFeatureVisible: true,
      featureType: type,
    });
  };

  hideEditItineraryFeatureModal = () => {
    this.setState({
      editItineraryFeatureVisible: false,
      featureType: undefined,
    });
  };

  openEditAmountModal = () => {
    this.setState({
      editAmountModalVisible: true,
    });
  };

  hideEditAmountModal = () => {
    this.setState({
      editAmountModalVisible: false,
    });
  };

  addFeature = (feature: ICreateItineraryFeatureFormValues) => {
    if (this.state.featureType === 'driver') {
      return this.addDriverFeature(feature);
    }
    if (this.state.featureType === 'vehicle') {
      return this.addVehicleFeature(feature);
    }
  };

  editFeature = (feature: ICreateItineraryFeatureFormValues) => {
    if (this.state.featureType === 'driver') {
      return this.editDriverFeature(feature);
    }
    if (this.state.featureType === 'vehicle') {
      return this.editVehicleFeature(feature);
    }
  };

  addDriverFeature = (feature: ICreateItineraryFeatureFormValues) => {
    const featureToAdd: FeatureCandidate = {
      ...feature,
      id: UUID.v4(),
    };

    this.setState({
      driverFeatures: [
        ...this.state.driverFeatures,
        featureToAdd,
      ] as FeatureCandidate[],
    });
    this.clearDriverFeaturesRowSelection();
  };

  editDriverFeature = (feature: ICreateItineraryFeatureFormValues) => {
    const driverFeatureBeingEdited = this.state.selectedDriverFeatures?.[0];

    const restOfTheDriverFeatures = this.state.driverFeatures.filter(
      (feature) => feature.id !== driverFeatureBeingEdited,
    );

    this.setState({
      driverFeatures: [
        ...restOfTheDriverFeatures,
        { id: driverFeatureBeingEdited, ...feature },
      ],
    });
    this.clearDriverFeaturesRowSelection();
  };

  removeDriverFeature = () => {
    const newDriverFeatures = (
      this.state.driverFeatures as FeatureCandidate[]
    ).filter(
      (feature) => !this.state.selectedDriverFeatures.includes(feature.id),
    );

    this.setState({
      driverFeatures: newDriverFeatures,
    });
    this.clearDriverFeaturesRowSelection();
  };

  duplicateDriverFeature = () => {
    const { selectedDriverFeatures, driverFeatures } = this.state;

    const featuresToDuplicate = (driverFeatures as FeatureCandidate[])
      .filter((feature) => selectedDriverFeatures.includes(feature.id))
      .map((feature) => {
        return {
          ...feature,
          id: UUID.v4(),
        };
      });

    this.setState({
      driverFeatures: [...this.state.driverFeatures, ...featuresToDuplicate],
    });
    this.clearDriverFeaturesRowSelection();
  };

  addVehicleFeature = (feature: ICreateItineraryFeatureFormValues) => {
    const featureToAdd: FeatureCandidate = {
      ...feature,
      id: UUID.v4(),
    };

    this.setState({
      vehicleFeatures: [
        ...this.state.vehicleFeatures,
        featureToAdd,
      ] as FeatureCandidate[],
    });
    this.clearVehicleFeaturesRowSelection();
  };

  editVehicleFeature = (feature: ICreateItineraryFeatureFormValues) => {
    const vehicleFeatureBeingEdited = this.state.selectedVehicleFeatures?.[0];

    const restOfTheVehicleFeatures = this.state.vehicleFeatures.filter(
      (feature) => feature.id !== vehicleFeatureBeingEdited,
    );

    this.setState({
      vehicleFeatures: [
        ...restOfTheVehicleFeatures,
        { id: vehicleFeatureBeingEdited, ...feature },
      ],
    });
    this.clearVehicleFeaturesRowSelection();
  };

  removeVehicleFeature = () => {
    const newVehicleFeatures = (
      this.state.vehicleFeatures as FeatureCandidate[]
    ).filter(
      (feature) => !this.state.selectedVehicleFeatures.includes(feature.id),
    );

    this.setState({
      vehicleFeatures: newVehicleFeatures,
    });
    this.clearVehicleFeaturesRowSelection();
  };

  duplicateVehicleFeature = () => {
    const { selectedVehicleFeatures, vehicleFeatures } = this.state;

    const featuresToDuplicate = (vehicleFeatures as FeatureCandidate[])
      .filter((feature) => selectedVehicleFeatures.includes(feature.id))
      .map((feature) => {
        return {
          ...feature,
          id: UUID.v4(),
        };
      });

    this.setState({
      vehicleFeatures: [...this.state.vehicleFeatures, ...featuresToDuplicate],
    });
    this.clearVehicleFeaturesRowSelection();
  };

  editAmount = (amount: ICreateAmountFormValues) => {
    const amountIdBeingEdited = this.state.selectedAmounts?.[0];

    const restOfTheAmounts = this.state.amounts.filter(
      (amount) => amount.id !== amountIdBeingEdited,
    );

    this.setState({
      amounts: [...restOfTheAmounts, { id: amountIdBeingEdited, ...amount }],
    });
    this.clearAmountsRowSelection();
  };

  addAmount = (amount: ICreateAmountFormValues) => {
    const amountToAdd: Amount = {
      ...amount,
      id: UUID.v4(),
    };

    this.setState({
      amounts: [...this.state.amounts, amountToAdd] as Amount[],
    });
    this.clearAmountsRowSelection();
  };

  removeAmount = () => {
    const newAmounts = (this.state.amounts as Amount[]).filter(
      (amount) => !this.state.selectedAmounts.includes(amount.id),
    );

    this.setState({
      amounts: newAmounts,
    });
    this.clearAmountsRowSelection();
  };

  duplicateAmount = () => {
    const { selectedAmounts, amounts } = this.state;

    const amountsToDublicate = (amounts as Amount[])
      .filter((amount) => selectedAmounts.includes(amount.id))
      .map((amount) => {
        return {
          ...amount,
          id: UUID.v4(),
        };
      });

    this.setState({
      amounts: [...this.state.amounts, ...amountsToDublicate],
    });
    this.clearAmountsRowSelection();
  };

  clearAmountsRowSelection = () => {
    this.setState({
      selectedAmounts: [],
    });
  };

  clearDriverFeaturesRowSelection = () => {
    this.setState({
      selectedDriverFeatures: [],
    });
  };

  clearVehicleFeaturesRowSelection = () => {
    this.setState({
      selectedVehicleFeatures: [],
    });
  };

  onAmountSelectChange = (selectedRowKeys: string[]) => {
    this.setState({ selectedAmounts: selectedRowKeys });
  };

  onDriverFeatureSelectChange = (selectedRowKeys: string[]) => {
    this.setState({ selectedDriverFeatures: selectedRowKeys });
  };

  onVehicleFeatureSelectChange = (selectedRowKeys: string[]) => {
    this.setState({ selectedVehicleFeatures: selectedRowKeys });
  };

  calculateAmount = async () => {
    if (!this.allowedToCalculateAmount()) {
      return;
    }

    message.loading({
      content: 'Calculando importe',
      key: 'calculate-amount',
    });

    this.setState({
      calculatingPrice: true,
      ...(this.state.amounts?.length && {
        amounts: [],
      }),
    });
    try {
      const departureDateTime = this.state.departureDate
        .clone()
        .set('hours', this.state.departureTime.get('hours'))
        .set('minutes', this.state.departureTime.get('minutes'));

      const arrivalDateTime = this.state.arrivalDate
        .clone()
        .set('hours', this.state.arrivalTime.get('hours'))
        .set('minutes', this.state.arrivalTime.get('minutes'));

      const selectedVehicleCategory = this.props.rate.vehicleCategories.find(
        (vc) => vc.id === this.state.selectedVehicleCategoryId,
      );

      const amount = await getClient().query<
        ComputeItineraryPriceQuery,
        ComputeItineraryPriceQueryVariables
      >({
        query: ComputeItineraryPriceDocument,
        fetchPolicy: 'no-cache',
        variables: {
          capacity: selectedVehicleCategory.vehicleCapacity,
          vehicleBrandId: selectedVehicleCategory.vehicleBrand?.id,
          vehicleModelId: selectedVehicleCategory.vehicleModel?.id,
          vehicleTypeId: selectedVehicleCategory.vehicleType?.id,
          clientId: this.props.clientId,
          hours: Math.ceil(
            moment.duration(arrivalDateTime.diff(departureDateTime)).asHours(),
          ),
          dinner: this.state.dinner,
          serviceTypeId: this.state.selectedServiceTypeId,
          departure: {
            googleMapsPlaceID: this.state.departureGoogleMapsPlaceID,
            isAirport: this.state.departureType === 'airport',
          },
          ...(this.state.provisionGoogleMapsPlaceID && {
            provision: {
              googleMapsPlaceID: this.state.provisionGoogleMapsPlaceID,
              isAirport: this.state.provisionType === 'airport',
            },
          }),
          arrival: {
            googleMapsPlaceID: this.state.arrivalGoogleMapsPlaceID,
            isAirport: this.state.arrivalType === 'airport',
          },
        },
      });

      if (
        amount?.data?.computeItineraryPrice?.consult ||
        amount?.data?.computeItineraryPrice?.amount === 0
      ) {
        const content = this.context.isAdmin
          ? 'Con la información proporcionada no es posible calcular el importe del itinerario automáticamente'
          : 'Con la información proporcionada no es posible calcular el importe del itinerario automáticamente. Puedes realizar una consulta a MVIPS haciendo click en el botón inferior';

        Modal.info({
          centered: true,
          maskClosable: false,
          title: 'No es posible computar el importe automáticamente',
          content: content,
          // cancelText: 'Cerrar',
        });
      }

      if (amount?.data?.computeItineraryPrice?.amount > 0) {
        this.setState({
          amounts: [
            ...this.state.amounts,
            {
              id: UUID.v4(),
              amount: amount?.data?.computeItineraryPrice?.amount,
              concept: 'Calculado automáticamente',
              vat: 0.1,
            },
          ],
        });
      }
    } catch (_) {
      message.error({
        content: 'Error al calcular importe',
        key: 'calculate-amount',
      });
    } finally {
      message.success({
        content: 'Importe calculado correctamente',
        key: 'calculate-amount',
      });
      this.setState({
        calculatingPrice: false,
      });
    }
  };

  allowedToCalculateAmount = (): boolean => {
    if (
      !(
        !!this.state.selectedServiceTypeId &&
        !!this.state.selectedServiceTypeCode
      )
    ) {
      return false;
    }

    if (
      !(
        !!this.state.departureDate &&
        !!this.state.departureTime &&
        !!this.state.arrivalDate &&
        !!this.state.arrivalTime &&
        !!this.props.clientId &&
        !!this.state.departureGoogleMapsPlaceID &&
        !!this.state.arrivalGoogleMapsPlaceID &&
        !!this.state.selectedVehicleCategoryId &&
        !!this.state.selectedServiceTypeId &&
        !!this.state.selectedServiceTypeCode
      )
    ) {
      return false;
    }

    // if (this.state.selectedServiceTypeCode === 'TR') {
    //   // Transfer
    // }

    if (this.state.selectedServiceTypeCode === 'DIS') {
      // Disposicion
      return !!this.state.provisionGoogleMapsPlaceID;
    }

    return true;
  };

  render(): React.ReactNode {
    const amountsColumns = [
      {
        title: 'Concepto',
        dataIndex: 'concept',
      },
      {
        title: 'Importe',
        dataIndex: 'amount',
        render: (value: number) => {
          return `${value.toFixed(2)}€`;
        },
      },
      {
        title: 'Tipo I.V.A',
        dataIndex: 'vat',
        render: (value: number) => {
          return `${value * 100}%`;
        },
      },
    ];

    const featuresColumns = [
      {
        title: 'Requerido',
        dataIndex: 'required',
        render: (value: boolean, feature: FeatureCandidate) => {
          if (feature.required) {
            return <Tag color={'green'}>{'Sí'}</Tag>;
          } else {
            return <Tag color={'volcano'}>{'No'}</Tag>;
          }
        },
      },
      {
        title: 'Descripción',
        dataIndex: 'description',
      },
    ];

    const { selectedAmounts, selectedDriverFeatures, selectedVehicleFeatures } =
      this.state;

    const amountsRowSelection = {
      selectedRowKeys: selectedAmounts,
      onChange: this.onAmountSelectChange,
    };

    const driverFeaturesRowSelection = {
      selectedRowKeys: selectedDriverFeatures,
      onChange: this.onDriverFeatureSelectChange,
    };

    const vehicleFeaturesRowSelection = {
      selectedRowKeys: selectedVehicleFeatures,
      onChange: this.onVehicleFeatureSelectChange,
    };

    const { getFieldDecorator } = this.props.form;

    return (
      <>
        <Form
          layout={'vertical'}
          onSubmit={this.handleSubmit}
          autoComplete={'false'}
        >
          <Row gutter={32}>
            <Col span={8}>
              <Form.Item label={'Referencia'}>
                {getFieldDecorator('reference', {
                  initialValue: this.props.itinerary?.reference,
                  rules: [
                    {
                      required: false,
                    },
                  ],
                })(
                  <Input
                    placeholder="Eg: X3234J"
                    disabled={
                      !!this.props.itinerary &&
                      !this.props.itinerary._notSubmitted
                    }
                  />,
                )}
              </Form.Item>

              <Form.Item
                label="Fecha de salida y de llegada"
                required
                style={{ marginRight: '15px' }}
              >
                {getFieldDecorator('departureArrivalDate', {
                  initialValue: [
                    this.state.departureDateTime,
                    this.state.arrivalDateTime,
                  ].filter((i) => !!i),
                  rules: [
                    {
                      required: true,
                      message: 'Por favor introduce la salida y la llegada',
                    },
                  ],
                })(
                  <DatePicker.RangePicker
                    format={'DD/MM/YYYY'}
                    onChange={([departure, arrival]) => {
                      this.setState(
                        {
                          departureDate: departure,
                          arrivalDate: arrival,
                        },
                        () => {
                          this.calculateAmount();
                        },
                      );
                    }}
                  />,
                )}
              </Form.Item>
              <Form.Item
                label="Hora de salida y de llegada"
                style={{ marginBottom: 0, flexDirection: 'row' }}
                required
              >
                {getFieldDecorator('departureArrivalTime', {
                  initialValue: [
                    this.state.departureDateTime,
                    this.state.arrivalDateTime,
                  ].filter((i) => !!i),
                  rules: [
                    {
                      required: true,
                      message: 'Por favor introduce una hora de salida',
                    },
                  ],
                })(
                  <TimePicker.RangePicker
                    placeholder={['Hora de salida', 'Hora de llegada']}
                    format={'HH:mm'}
                    onChange={([departure, arrival]) => {
                      this.setState(
                        {
                          departureTime: departure,
                          arrivalTime: arrival,
                        },
                        () => {
                          this.calculateAmount();
                        },
                      );
                    }}
                  />,
                )}
                {/* <Form.Item required style={{ marginRight: '15px' }}>
                    {getFieldDecorator('departureTime', {
                      initialValue: this.props.itinerary?.departureTime,
                      rules: [
                        {
                          required: true,
                          message: 'Por favor introduce una hora de salida',
                        },
                      ],
                    })(<TimePicker placeholder={'Salida'} format={'HH:mm'} />)}
                  </Form.Item>
                  <Form.Item required>
                    {getFieldDecorator('arrivalTime', {
                      initialValue: this.props.itinerary?.arrivalTime,
                      rules: [
                        {
                          required: true,
                          message: 'Por favor introduce una hora de llegada',
                        },
                      ],
                    })(<TimePicker placeholder={'Llegada'} format={'HH:mm'} />)}
                  </Form.Item> */}
              </Form.Item>
              {/*<Form.Item label={'Hora de presentación'} required={true}>*/}
              {/*  {getFieldDecorator('presentationTime', {*/}
              {/*    initialValue: this.props.itinerary?.presentationTime,*/}
              {/*    rules: [*/}
              {/*      {*/}
              {/*        required: true,*/}
              {/*        message: 'Por favor introduce una hora de presentación',*/}
              {/*      },*/}
              {/*    ],*/}
              {/*  })(*/}
              {/*    <TimePicker*/}
              {/*      size={'default'}*/}
              {/*      placeholder={'Presentación'}*/}
              {/*      format={'HH:mm'}*/}
              {/*    />,*/}
              {/*  )}*/}
              {/*</Form.Item>*/}
              <Form.Item label={'Descripción'}>
                {getFieldDecorator('itineraryDescription', {
                  initialValue: this.props.itinerary?.itineraryDescription,
                  rules: [
                    {
                      required: true,
                      message: 'Necesitas introducir una descripción',
                    },
                  ],
                })(
                  <Input.TextArea
                    placeholder="Escribe una descripción del itinerario"
                    autoSize={{ minRows: 3, maxRows: 5 }}
                  />,
                )}
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Tipo de vehículo" required={true}>
                {getFieldDecorator('vehicleCategoryId', {
                  initialValue: this.state.selectedVehicleCategoryId,
                  rules: [
                    {
                      required: true,
                      message: 'Por favor, introduce un tipo de vehículo',
                    },
                  ],
                })(
                  <Select
                    showSearch={true}
                    placeholder={'Selecciona un tipo de vehículo'}
                    onSelect={(value: string) => {
                      this.setState({
                        selectedVehicleCategoryId: value,
                      });
                    }}
                  >
                    {this.props.rate.vehicleCategories.map((vc) => {
                      return (
                        // @ts-ignore
                        <Select.Option key={vc.id} value={vc.id}>
                          {capitalizeFirstChar(vc.name)?.toUpperCase()}
                        </Select.Option>
                      );
                    })}
                  </Select>,
                )}
              </Form.Item>

              {/* <Col span={6}>
                  <Form.Item label={'Tipo de vehículo'} required={true}>
                    {getFieldDecorator('vehicleTypeId', {
                      initialValue: this.state.selectedVehicleTypeId,
                      rules: [
                        {
                          required: true,
                          message: 'Por favor, introduce un tipo de vehículo',
                        },
                      ],
                    })(
                      <Select
                        allowClear={true}
                        showSearch={true}
                        placeholder={'Selecciona un tipo de vehículo'}
                        onSelect={(value: string) => {
                          this.setState({
                            selectedVehicleTypeId: value,
                          });
                        }}
                        onDeselect={() => {
                          this.setState({
                            selectedVehicleTypeId: undefined,
                          });
                        }}
                        filterOption={(inputValue, option) => {
                          const vehicleId = option.props.value;
                          const vehicle = this.props.allVehicleTypes.find(
                            (vehicle) => vehicle.id === vehicleId,
                          );

                          return vehicle.name
                            .toLowerCase()
                            .includes(inputValue.toLowerCase());
                        }}
                      >
                        {this.props.allVehicleTypes.map((vt) => {
                          return (
                            // @ts-ignore
                            <Select.Option key={vt.id} value={vt.id}>
                              {capitalizeFirstChar(vt.name)?.toUpperCase()}
                            </Select.Option>
                          );
                        })}
                      </Select>,
                    )}
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label={'Marca de vehiculo'}>
                    <Select
                      value={this.state.selectedVehicleBrand}
                      placeholder={'Mercedes, Comanche..'}
                      allowClear={true}
                      // dropdownMatchSelectWidth={false}
                      dropdownStyle={{ width: '200px' }}
                      onSelect={(value) => {
                        this.setState({
                          selectedVehicleBrand: value as string,
                        });
                      }}
                      onDeselect={(value) => {
                        this.setState({
                          selectedVehicleBrand: null,
                          selectedVehicleModel: null,
                        });
                      }}
                      onChange={(value) => {
                        if (!value) {
                          this.setState({
                            selectedVehicleBrand: null,
                            selectedVehicleModel: null,
                          });
                        }
                      }}
                    >
                      {this.props.allVehicleBrandModels.map((brandModels) => {
                        return (
                          <Select.Option
                            key={brandModels.id}
                            value={brandModels.id}
                          >
                            {brandModels.brand.toUpperCase()}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label={'Modelo de vehiculo'}>
                    <Select
                      disabled={!this.state.selectedVehicleBrand}
                      value={this.state.selectedVehicleModel}
                      placeholder={'Maybach, Vito..'}
                      allowClear={true}
                      // dropdownMatchSelectWidth={false}
                      dropdownStyle={{ width: '200px' }}
                      onSelect={(value) => {
                        this.setState({
                          selectedVehicleModel: value as string,
                        });
                      }}
                      onDeselect={(value) => {
                        this.setState({
                          selectedVehicleModel: undefined,
                        });
                      }}
                    >
                      {models.map((model) => {
                        return (
                          <Select.Option key={model.id} value={model.id}>
                            {model.name.toUpperCase()}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label={'Capacidad (PAX)'} style={{ flex: 1 }}>
                    <Select
                      // disabled={
                      //   !this.state.selectedVehicleBrand ||
                      //   !this.state.selectedVehicleModel ||
                      //   !this.state.selectedVehicleTypeId
                      // }
                      value={this.state.selectedVehicleCapacity}
                      placeholder={'19, 28, 50'}
                      allowClear={true}
                      // dropdownMatchSelectWidth={false}
                      dropdownStyle={{ width: '200px' }}
                      onClear={() => {
                        this.setState({
                          selectedVehicleCapacity: null,
                        });
                      }}
                      onSelect={(value) => {
                        this.setState({
                          selectedVehicleCapacity: value as number,
                        });
                      }}
                      onDeselect={(value) => {
                        this.setState({
                          selectedVehicleCapacity: null,
                        });
                      }}
                    >
                      {[19, 28, 50].map((capacity, index) => {
                        return (
                          <Select.Option key={index} value={capacity}>
                            {capacity}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col> */}

              <div style={{ display: 'flex' }}>
                <Form.Item
                  label={'Número de vehículos'}
                  style={{ marginRight: '15px', flex: 1 }}
                >
                  {getFieldDecorator('numVehicles', {
                    initialValue: this.props.itinerary?.numVehicles || 1,
                  })(
                    <InputNumber
                      min={1}
                      precision={0}
                      autoComplete={'cc-csc'}
                    />,
                  )}
                </Form.Item>
                <Form.Item label={'# Pasajeros'} style={{ flex: 1 }}>
                  {getFieldDecorator('numPassengers', {
                    initialValue: this.props.itinerary?.numPassengers || 1,
                  })(
                    <InputNumber
                      onChange={(value) => {
                        this.setState({
                          selectedNumPassengers: value,
                        });
                      }}
                      min={1}
                      autoComplete={'cc-csc'}
                    />,
                  )}
                </Form.Item>
              </div>

              {/*<Form.Item label={'Ubicación de salida'}>*/}
              {/*  <Input.Group compact>*/}
              {/*    {getFieldDecorator('departureAddress', {*/}
              {/*      initialValue: this.props.itinerary?.departureAddress,*/}
              {/*      rules: [*/}
              {/*        {*/}
              {/*          required: false,*/}
              {/*        },*/}
              {/*        {*/}
              {/*          whitespace: true,*/}
              {/*          message:*/}
              {/*            'La dirección no puede contener únicamente carácteres en blanco',*/}
              {/*        },*/}
              {/*      ],*/}
              {/*    })(*/}
              {/*      <Input*/}
              {/*        style={{ width: '50%' }}*/}
              {/*        placeholder={'Dirección'}*/}
              {/*      />,*/}
              {/*    )}*/}
              {/*    {getFieldDecorator('departureLocation', {*/}
              {/*      initialValue: this.props.itinerary?.departureLocation,*/}
              {/*      rules: [*/}
              {/*        {*/}
              {/*          required: false,*/}
              {/*        },*/}
              {/*        {*/}
              {/*          whitespace: true,*/}
              {/*          message:*/}
              {/*            'La población no puede contener únicamente carácteres en blanco',*/}
              {/*        },*/}
              {/*      ],*/}
              {/*    })(*/}
              {/*      <Input*/}
              {/*        style={{ width: '50%' }}*/}
              {/*        placeholder={'Población'}*/}
              {/*      />,*/}
              {/*    )}*/}
              {/*  </Input.Group>*/}
              {/*</Form.Item>*/}
              {/*<Form.Item label={'Ubicación de llegada'}>*/}
              {/*  <Input.Group compact>*/}
              {/*    {getFieldDecorator('arrivalAddress', {*/}
              {/*      initialValue: this.props.itinerary?.arrivalAddress,*/}
              {/*      rules: [*/}
              {/*        {*/}
              {/*          required: false,*/}
              {/*        },*/}
              {/*      ],*/}
              {/*    })(*/}
              {/*      <Input*/}
              {/*        style={{ width: '50%' }}*/}
              {/*        placeholder={'Dirección'}*/}
              {/*      />,*/}
              {/*    )}*/}
              {/*    {getFieldDecorator('arrivalLocation', {*/}
              {/*      initialValue: this.props.itinerary?.arrivalLocation,*/}
              {/*      rules: [*/}
              {/*        {*/}
              {/*          required: false,*/}
              {/*        },*/}
              {/*      ],*/}
              {/*    })(*/}
              {/*      <Input*/}
              {/*        style={{ width: '50%' }}*/}
              {/*        placeholder={'Población'}*/}
              {/*      />,*/}
              {/*    )}*/}
              {/*  </Input.Group>*/}
              {/*</Form.Item>*/}
            </Col>
            <Col span={8}>
              <Form.Item label={'Tipo de servicio'} required>
                {getFieldDecorator('serviceTypeId', {
                  initialValue: this.state.selectedServiceTypeId,
                  rules: [
                    {
                      required: true,
                      message: 'Por favor, selecciona un tipo de servicio',
                    },
                  ],
                })(
                  <Select
                    placeholder={'Selecciona un tipo de servicio'}
                    onSelect={async (value) => {
                      // await this.fetchServiceSubtypes(value as string);

                      this.setState({
                        selectedServiceTypeCode: this.props.serviceTypes.find(
                          (st) => st.id === value,
                        ).typeId,
                        selectedServiceTypeId: value as string,
                      });
                    }}
                    onDeselect={(value) => {
                      this.setState({
                        selectedServiceTypeCode: undefined,
                        selectedServiceTypeId: undefined,
                      });
                    }}
                  >
                    {this.props.serviceTypes
                      .filter((st) => st.typeId === 'TR' || st.typeId === 'DIS')
                      .map((serviceType) => {
                        return (
                          <Select.Option
                            key={serviceType.id}
                            value={serviceType.id}
                          >
                            {serviceType.name.toUpperCase()}
                          </Select.Option>
                        );
                      })}
                  </Select>,
                )}
              </Form.Item>
              {this.state.selectedServiceTypeCode === 'DIS' && (
                <Form.Item label={'Cena'}>
                  {getFieldDecorator('dinner', {
                    initialValue: this.state.dinner,
                  })(
                    <Switch
                      checked={this.state.dinner}
                      onChange={(checked) => {
                        this.setState({
                          dinner: checked,
                        });
                      }}
                    />,
                  )}
                </Form.Item>
              )}
            </Col>
          </Row>
          <Divider />
          <Row
            gutter={32}
            style={{
              flexDirection: 'row-reverse',
            }}
          >
            <Col span={12}>
              <Form.Item
                label={'Lugar de salida'}
                required={true}
                validateStatus={
                  !this.state.departureGoogleMapsPlaceID ? 'error' : ''
                }
                {...(!this.state.departureGoogleMapsPlaceID && {
                  validateStatus: 'error',
                  help: 'Por favor, indica un lugar de salida',
                })}
              >
                <GooglePlacesAutocomplete
                  apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                  apiOptions={{
                    language: 'es',
                    region: 'es',
                  }}
                  autocompletionRequest={{
                    componentRestrictions: {
                      country: ['es'],
                    },
                  }}
                  selectProps={{
                    placeholder: 'Escribe una dirección de salida',
                    isClearable: true,
                    styles: {
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999,
                      }),
                    },
                    defaultValue: this.state.departureAddress
                      ? {
                          label: this.state.departureAddress,
                          value: this.state.departureAddress,
                        }
                      : null,
                    onChange: async (value) => {
                      if (!value) {
                        this.setState({
                          departureGoogleMapsPlaceID: undefined,
                          departureLat: undefined,
                          departureLon: undefined,
                          departureType: undefined,
                          departureAddress: undefined,
                          departureCity: undefined,
                        });
                        return;
                      }

                      const {
                        place_id: placeID,
                        description,
                        address_components,
                        types,
                        terms,
                      } = value.value;

                      const [placeInfo] = await geocodeByPlaceId(placeID);

                      if (!placeInfo) {
                        return;
                      }

                      const city = types.includes('locality')
                        ? terms[types.findIndex((t) => t === 'locality')].value
                        : address_components?.find((ac) =>
                            ac.types.find((t) => t === 'locality'),
                          )?.long_name || null;

                      this.setState({
                        departureGoogleMapsPlaceID: placeID,
                        departureLat: placeInfo.geometry.location.lat(),
                        departureLon: placeInfo.geometry.location.lng(),
                        departureType: placeInfo.types?.[0],
                        departureAddress: description,
                        departureCity: city,
                      });
                    },
                  }}
                />
                <Checkbox
                  disabled={!this.state.departureGoogleMapsPlaceID}
                  style={{ marginTop: '0.5em' }}
                  checked={this.state.departureType === 'airport'}
                  onChange={(e) => {
                    this.setState({
                      departureType: e.target.checked ? 'airport' : undefined,
                    });
                  }}
                >
                  Es aeropuerto
                </Checkbox>
              </Form.Item>

              {this.state.selectedServiceTypeCode === 'DIS' && (
                <Form.Item
                  label={'Lugar de disposición'}
                  required={true}
                  validateStatus={
                    !this.state.provisionGoogleMapsPlaceID ? 'error' : ''
                  }
                  {...(!this.state.provisionGoogleMapsPlaceID && {
                    validateStatus: 'error',
                    help: 'Por favor, indica un lugar de disposición',
                  })}
                >
                  <GooglePlacesAutocomplete
                    apiOptions={{
                      language: 'es',
                      region: 'es',
                    }}
                    apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                    autocompletionRequest={{
                      componentRestrictions: {
                        country: ['es'],
                      },
                    }}
                    selectProps={{
                      placeholder: 'Escribe una dirección de disposición',
                      isClearable: true,
                      styles: {
                        menu: (provided) => ({
                          ...provided,
                          zIndex: 9999,
                        }),
                      },
                      defaultValue: this.state.provisionAddress
                        ? {
                            label: this.state.provisionAddress,
                            value: this.state.provisionAddress,
                          }
                        : null,
                      onChange: async (value) => {
                        if (!value) {
                          this.setState({
                            provisionGoogleMapsPlaceID: undefined,
                            provisionLat: undefined,
                            provisionLon: undefined,
                            provisionType: undefined,
                            provisionAddress: undefined,
                            provisionCity: undefined,
                          });
                          return;
                        }

                        const {
                          place_id: placeID,
                          description,
                          address_components,
                          types,
                          terms,
                        } = value.value;

                        const [placeInfo] = await geocodeByPlaceId(placeID);

                        if (!placeInfo) {
                          return;
                        }

                        const city = types.includes('locality')
                          ? terms[types.findIndex((t) => t === 'locality')]
                              .value
                          : address_components?.find((ac) =>
                              ac.types.find((t) => t === 'locality'),
                            )?.long_name || null;

                        this.setState({
                          provisionGoogleMapsPlaceID: placeID,
                          provisionLat: placeInfo.geometry.location.lat(),
                          provisionLon: placeInfo.geometry.location.lng(),
                          provisionType: placeInfo.types?.[0],
                          provisionAddress: description,
                          provisionCity: city,
                        });
                      },
                    }}
                  />
                  <Checkbox
                    disabled={!this.state.provisionGoogleMapsPlaceID}
                    style={{ marginTop: '0.5em' }}
                    checked={this.state.provisionType === 'airport'}
                    onChange={(e) => {
                      this.setState({
                        provisionType: e.target.checked ? 'airport' : undefined,
                      });
                    }}
                  >
                    Es aeropuerto
                  </Checkbox>
                </Form.Item>
              )}

              <Form.Item
                label={'Lugar de llegada'}
                required={true}
                validateStatus={
                  !this.state.arrivalGoogleMapsPlaceID ? 'error' : ''
                }
                {...(!this.state.arrivalGoogleMapsPlaceID && {
                  validateStatus: 'error',
                  help: 'Por favor, indica un lugar de llegada',
                })}
              >
                <GooglePlacesAutocomplete
                  apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                  apiOptions={{
                    language: 'es',
                    region: 'es',
                  }}
                  autocompletionRequest={{
                    componentRestrictions: {
                      country: ['es'],
                    },
                  }}
                  selectProps={{
                    placeholder: 'Escribe una dirección de llegada',
                    isClearable: true,
                    styles: {
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999,
                      }),
                    },
                    defaultValue: this.state.arrivalAddress
                      ? {
                          label: this.state.arrivalAddress,
                          value: this.state.arrivalAddress,
                        }
                      : null,
                    onChange: async (value) => {
                      if (!value) {
                        this.setState({
                          arrivalGoogleMapsPlaceID: undefined,
                          arrivalLat: undefined,
                          arrivalLon: undefined,
                          arrivalType: undefined,
                          arrivalAddress: undefined,
                          arrivalCity: undefined,
                        });
                        return;
                      }

                      const {
                        place_id: placeID,
                        description,
                        address_components,
                        types,
                        terms,
                      } = value.value;

                      const [placeInfo] = await geocodeByPlaceId(placeID);

                      if (!placeInfo) {
                        return;
                      }

                      const city = types.includes('locality')
                        ? terms[types.findIndex((t) => t === 'locality')].value
                        : address_components?.find((ac) =>
                            ac.types.find((t) => t === 'locality'),
                          )?.long_name || null;

                      this.setState({
                        arrivalGoogleMapsPlaceID: placeID,
                        arrivalLat: placeInfo.geometry.location.lat(),
                        arrivalLon: placeInfo.geometry.location.lng(),
                        arrivalType: placeInfo.types?.[0],
                        arrivalAddress: description,
                        arrivalCity: city,
                      });
                    },
                  }}
                />
                <Checkbox
                  disabled={!this.state.arrivalGoogleMapsPlaceID}
                  style={{ marginTop: '0.5em' }}
                  checked={this.state.arrivalType === 'airport'}
                  onChange={(e) => {
                    this.setState({
                      arrivalType: e.target.checked ? 'airport' : undefined,
                    });
                  }}
                >
                  Es aeropuerto
                </Checkbox>
              </Form.Item>
              {this.state.estimatedDistanceInMeters && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <h3>{`Distancia: ${Math.floor(
                    this.state.estimatedDistanceInMeters / 1000,
                  )} km`}</h3>
                </div>
              )}
              <MiniMap
                onDistanceFound={(distance) => {
                  this.setState({
                    estimatedDistanceInMeters: distance,
                  });
                }}
                provision={
                  this.state.provisionLat && this.state.provisionLon
                    ? {
                        lat: this.state.provisionLat,
                        lon: this.state.provisionLon,
                      }
                    : undefined
                }
                departure={
                  this.state.departureLat && this.state.departureLon
                    ? {
                        lat: this.state.departureLat,
                        lon: this.state.departureLon,
                      }
                    : undefined
                }
                arrival={
                  this.state.arrivalLat && this.state.arrivalLon
                    ? {
                        lat: this.state.arrivalLat,
                        lon: this.state.arrivalLon,
                      }
                    : undefined
                }
              />
            </Col>
            <Col span={12}>
              <Row>
                <Col span={24} tabIndex={-1}>
                  <Tabs type="card" defaultActiveKey={'amounts'}>
                    <Tabs.TabPane tab="Itinerario" key="itinerary">
                      <Row gutter={32}>
                        <Col span={6}>
                          <Form.Item label={'Nombre del grupo'}>
                            {getFieldDecorator('groupName', {
                              initialValue: this.props.itinerary?.groupName,
                            })(<Input />)}
                          </Form.Item>
                        </Col>
                        <Col span={6} offset={12}>
                          <Form.Item label={'# de vuelo o tren'}>
                            {getFieldDecorator('flightNumber', {
                              initialValue: this.props.itinerary?.flightNumber,
                            })(<Input placeholder="Eg: DL1994" />)}
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.Item label={'Notas'}>
                        {getFieldDecorator('notes', {
                          initialValue: this.props.itinerary?.notes,
                        })(
                          <Input.TextArea
                            placeholder="Puedes añadir una nota"
                            autoSize={{ minRows: 2, maxRows: 3 }}
                          />,
                        )}
                      </Form.Item>
                    </Tabs.TabPane>
                    <Tabs.TabPane tab="Importes" key="amounts">
                      <Alert
                        style={{ marginBottom: '2em' }}
                        closable={true}
                        type={'warning'}
                        message={
                          'Para poder calcular el importe automáticamente' +
                          ' necesitas seleccionar como mínimo el tipo de vehículo, además de el tipo de servicio y el lugar de salida y de llegada.' +
                          ' Opcionalmente puedes indicar el número de pasajeros, que dependiendo del tipo de vehículo influirá en el precio computado,'
                        }
                      />

                      <Form.Item>
                        <div
                          style={{
                            flex: 1,
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            marginBottom: 20,
                            display: 'flex',
                          }}
                        >
                          <div style={{ flex: 1, display: 'flex' }}>
                            {this.context.isAdmin && (
                              <Button
                                disabled={
                                  this.state.selectedAmounts?.length === 0
                                }
                                type={'ghost'}
                                icon={<LegacyIcon type={'copy'} />}
                                size={'small'}
                                onClick={() => this.duplicateAmount()}
                              >
                                Duplicar
                              </Button>
                            )}
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              flex: 1,
                              flexDirection: 'row',
                              justifyContent: 'flex-end',
                            }}
                          >
                            <Button
                              disabled={
                                this.state.selectedAmounts?.length === 0
                              }
                              danger
                              icon={<LegacyIcon type={'close'} />}
                              size={'small'}
                              onClick={() => this.removeAmount()}
                            >
                              Eliminar
                            </Button>
                            {this.context.isAdmin && (
                              <>
                                <Button
                                  disabled={
                                    !(this.state.selectedAmounts?.length === 1)
                                  }
                                  style={{ marginLeft: 10 }}
                                  icon={<LegacyIcon type={'edit'} />}
                                  size={'small'}
                                  onClick={() => this.openEditAmountModal()}
                                >
                                  Modificar
                                </Button>
                                <Button
                                  onClick={this.openCreateAmountModal}
                                  size={'small'}
                                  style={{ marginLeft: 10 }}
                                  icon={<LegacyIcon type={'plus'} />}
                                  type={'primary'}
                                >
                                  Nuevo
                                </Button>
                              </>
                            )}
                            <Button
                              loading={this.state.calculatingPrice}
                              size={'small'}
                              style={{ marginLeft: 10 }}
                              type={'primary'}
                              onClick={this.calculateAmount}
                              disabled={!this.allowedToCalculateAmount()}
                            >
                              {this.state.amounts?.length
                                ? 'Recalcular importe'
                                : 'Calcular importe'}
                            </Button>
                          </div>
                        </div>

                        <Table
                          rowKey={(record: Amount) => record.id}
                          size={'small'}
                          bordered={true}
                          rowSelection={amountsRowSelection}
                          columns={amountsColumns}
                          dataSource={this.state.amounts}
                          pagination={{ pageSize: 5 }}
                          locale={{
                            emptyText: (
                              <Empty
                                description="No hay importes todavía"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                              />
                            ),
                          }}
                        />
                      </Form.Item>

                      <Row>
                        <Col span={12} offset={12}>
                          <Form.Item>
                            <Descriptions
                              bordered={true}
                              size={'small'}
                              column={1}
                            >
                              <Descriptions.Item label={'Suma de importes'}>
                                {this.state.amounts
                                  .reduce(
                                    (total: number, currentValue: Amount) => {
                                      return total + currentValue.amount;
                                    },
                                    0,
                                  )
                                  .toFixed(2)}
                                {' €'}
                              </Descriptions.Item>
                              <Descriptions.Item label={'Base imponible'}>
                                {this.state.amounts
                                  .reduce(
                                    (total: number, currentValue: Amount) => {
                                      return total + currentValue.amount;
                                    },
                                    0,
                                  )
                                  .toFixed(2)}
                                {' €'}
                              </Descriptions.Item>
                              <Descriptions.Item label={'Importe total I.V.A'}>
                                {this.state.amounts
                                  .reduce(
                                    (total: number, currentValue: Amount) => {
                                      return (
                                        total +
                                        AmountUtils.calculateVATAmount(
                                          currentValue,
                                        )
                                      );
                                    },
                                    0,
                                  )
                                  .toFixed(2)}
                                {' €'}
                              </Descriptions.Item>
                              <Descriptions.Item label={'Total'}>
                                {(
                                  this.state.amounts.reduce(
                                    (total: number, currentValue: Amount) => {
                                      return total + currentValue.amount;
                                    },
                                    0,
                                  ) +
                                  this.state.amounts.reduce(
                                    (total: number, currentValue: Amount) => {
                                      return (
                                        total +
                                        AmountUtils.calculateVATAmount(
                                          currentValue,
                                        )
                                      );
                                    },
                                    0,
                                  )
                                ).toFixed(2)}
                                {' €'}
                              </Descriptions.Item>
                            </Descriptions>
                          </Form.Item>
                        </Col>
                      </Row>
                    </Tabs.TabPane>
                    <Tabs.TabPane tab="Características" key="features">
                      <Collapse
                        accordion
                        bordered={false}
                        defaultActiveKey={['1']}
                      >
                        <Collapse.Panel
                          header={'Características vehículos'}
                          key={1}
                        >
                          <div
                            style={{
                              flex: 1,
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                              marginBottom: 20,
                              display: 'flex',
                            }}
                          >
                            <div style={{ flex: 1, display: 'flex' }}>
                              <Button
                                disabled={
                                  this.state.selectedVehicleFeatures?.length ===
                                  0
                                }
                                type={'ghost'}
                                icon={<LegacyIcon type={'copy'} />}
                                size={'small'}
                                onClick={this.duplicateVehicleFeature}
                              >
                                Duplicar
                              </Button>

                              {/*<div style={{ marginLeft: 10 }}>*/}
                              {/*  <Button type={'dashed'} size={'default'}>*/}
                              {/*    Requerido*/}
                              {/*  </Button>*/}
                              {/*  <Button*/}
                              {/*    type={'dashed'}*/}
                              {/*    size={'default'}*/}
                              {/*    style={{ marginLeft: 5 }}*/}
                              {/*  >*/}
                              {/*    No requerido*/}
                              {/*  </Button>*/}
                              {/*</div>*/}
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'row',
                                justifyContent: 'flex-end',
                              }}
                            >
                              <Button
                                disabled={
                                  this.state.selectedVehicleFeatures?.length ===
                                  0
                                }
                                danger
                                icon={<LegacyIcon type={'close'} />}
                                size={'small'}
                                onClick={this.removeVehicleFeature}
                              >
                                Eliminar
                              </Button>
                              <Button
                                disabled={
                                  this.state.selectedVehicleFeatures?.length !==
                                  1
                                }
                                onClick={() =>
                                  this.openEditItineraryFeatureModal('vehicle')
                                }
                                style={{ marginLeft: 10 }}
                                icon={<LegacyIcon type={'edit'} />}
                                size={'small'}
                              >
                                Modificar
                              </Button>
                              <Button
                                onClick={() =>
                                  this.openCreateItineraryFeatureModal(
                                    'vehicle',
                                  )
                                }
                                size={'small'}
                                style={{ marginLeft: 10 }}
                                icon={<LegacyIcon type={'plus'} />}
                                type={'primary'}
                              >
                                Nueva
                              </Button>
                            </div>
                          </div>
                          <Table
                            size={'small'}
                            rowKey={(record: FeatureCandidate) => record.id}
                            rowSelection={vehicleFeaturesRowSelection}
                            columns={featuresColumns}
                            dataSource={this.state.vehicleFeatures}
                            bordered={true}
                            locale={{
                              emptyText: (
                                <Empty
                                  description="No hay características de vehículos todavía"
                                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                                />
                              ),
                            }}
                          />
                        </Collapse.Panel>
                        <Collapse.Panel
                          header={'Características conductores'}
                          key={2}
                        >
                          <div
                            style={{
                              flex: 1,
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                              marginBottom: 20,
                              display: 'flex',
                            }}
                          >
                            <div style={{ flex: 1, display: 'flex' }}>
                              <Button
                                disabled={
                                  this.state.selectedDriverFeatures?.length ===
                                  0
                                }
                                type={'ghost'}
                                icon={<LegacyIcon type={'copy'} />}
                                size={'small'}
                                onClick={this.duplicateDriverFeature}
                              >
                                Duplicar
                              </Button>
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'row',
                                justifyContent: 'flex-end',
                              }}
                            >
                              <Button
                                disabled={
                                  this.state.selectedDriverFeatures?.length ===
                                  0
                                }
                                danger
                                icon={<LegacyIcon type={'close'} />}
                                size={'small'}
                                onClick={this.removeDriverFeature}
                              >
                                Eliminar
                              </Button>
                              <Button
                                disabled={
                                  this.state.selectedDriverFeatures?.length !==
                                  1
                                }
                                onClick={() =>
                                  this.openEditItineraryFeatureModal('driver')
                                }
                                style={{ marginLeft: 10 }}
                                icon={<LegacyIcon type={'edit'} />}
                                size={'small'}
                              >
                                Modificar
                              </Button>
                              <Button
                                onClick={() =>
                                  this.openCreateItineraryFeatureModal('driver')
                                }
                                size={'small'}
                                style={{ marginLeft: 10 }}
                                icon={<LegacyIcon type={'plus'} />}
                                type={'primary'}
                              >
                                Nueva
                              </Button>
                            </div>
                          </div>
                          <Table
                            size={'small'}
                            rowKey={(record: FeatureCandidate) => record.id}
                            columns={featuresColumns}
                            bordered={true}
                            rowSelection={driverFeaturesRowSelection}
                            dataSource={this.state.driverFeatures}
                            locale={{
                              emptyText: (
                                <Empty
                                  description="No hay características de conductores todavía"
                                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                                />
                              ),
                            }}
                          />
                        </Collapse.Panel>
                      </Collapse>
                    </Tabs.TabPane>
                  </Tabs>
                </Col>
              </Row>
            </Col>
          </Row>
          <StickyFooterFormButtons>
            <Button
              size={'large'}
              style={{ marginRight: '15px' }}
              danger
              onClick={() => this.props.close()}
            >
              {this.props.itinerary ? 'Cerrar sin guardar' : 'Atrás'}
            </Button>
            <Button
              size={'large'}
              style={{ float: 'right' }}
              type="primary"
              htmlType="submit"
            >
              {this.props.itinerary ? 'Guardar cambios' : 'Crear itinerario'}
            </Button>
          </StickyFooterFormButtons>
        </Form>
        <EditAmount
          vatTypes={this.props.vatTypes}
          amount={this.state.amounts.find(
            (amount) => amount.id === this.state.selectedAmounts?.[0],
          )}
          editing={true}
          visible={this.state.editAmountModalVisible}
          onOk={this.hideEditAmountModal}
          onCancel={this.hideEditAmountModal}
          onSubmit={(value) => {
            this.editAmount(value);
            this.hideEditAmountModal();
          }}
        />
        <CreateAmount
          vatTypes={this.props.vatTypes}
          visible={this.state.createAmountModalVisible}
          onOk={this.hideCreateAmountModal}
          onCancel={this.hideCreateAmountModal}
          onSubmit={(value) => {
            this.addAmount(value);
            this.hideCreateAmountModal();
          }}
        />
        <CreateItineraryFeature
          onSubmit={(value) => {
            this.addFeature(value);
            this.hideCreateItineraryFeatureModal();
          }}
          visible={this.state.createItineraryFeatureVisible}
          onCancel={this.hideCreateItineraryFeatureModal}
          onOk={this.hideCreateItineraryFeatureModal}
        />
        <EditItineraryFeature
          feature={(() => {
            if (this.state.featureType === 'vehicle') {
              return this.state.vehicleFeatures.find(
                (feature) =>
                  feature.id === this.state.selectedVehicleFeatures?.[0],
              );
            } else if (this.state.featureType === 'driver') {
              return this.state.driverFeatures.find(
                (feature) =>
                  feature.id === this.state.selectedDriverFeatures?.[0],
              );
            }
          })()}
          onSubmit={(value) => {
            this.editFeature(value);
            this.hideEditItineraryFeatureModal();
          }}
          editing={true}
          visible={this.state.editItineraryFeatureVisible}
          onCancel={this.hideEditItineraryFeatureModal}
          onOk={this.hideEditItineraryFeatureModal}
        />
      </>
    );
  }
}

const MyCreateItineraryForm =
  Form.create<ICreateItineraryFormProps>()(CreateItineraryForm);

export default MyCreateItineraryForm;
